import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const GoogleMalwareReporter = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showGuide, setShowGuide] = useState(true);

  const phaCategoryOptions = [
    'Click Fraud',
    'Data Collection',
    'Elevated Privilege Abuse',
    'Hostile Downloader',
    'Phishing',
    'Ransomware',
    'SMS Fraud',
    'Spam',
    'Spyware',
    'Toll Fraud',
    'Trojan',
    'Other'
  ];

  const [formData, setFormData] = useState({
    packageNames: '',
    email: '',
    name: '',
    organization: '',
    isOnPlayStore: 'Yes',
    sha256OrLink: '',
    suspectedCategory: 'Spyware',
    details: '',
    hasPublicationLink: 'No'
  });

  // Google Form fields map for pre-fill
  const googleFormUrl = 'https://developers.google.com/android/play-protect/pha-reporting';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.packageNames.trim()) {
      alert('Please enter at least one package name');
      return;
    }

    if (!formData.email.trim()) {
      alert('Please enter your email');
      return;
    }

    if (!formData.name.trim()) {
      alert('Please enter your name');
      return;
    }

    if (!formData.sha256OrLink.trim()) {
      alert('Please enter SHA256 digest or Play Store link');
      return;
    }

    if (!formData.details.trim()) {
      alert('Please enter details about the issue');
      return;
    }

    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      // Package names as array
      const packageNamesArray = formData.packageNames
        .split(',')
        .map(pkg => pkg.trim())
        .filter(pkg => pkg);

      const response = await fetch(`${API_BASE_URL}/google-malware`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          packageNames: packageNamesArray,
          email: formData.email,
          name: formData.name,
          organization: formData.organization,
          isOnPlayStore: formData.isOnPlayStore,
          sha256OrLink: formData.sha256OrLink,
          suspectedCategory: formData.suspectedCategory,
          details: formData.details,
          hasPublicationLink: formData.hasPublicationLink
        })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Google Malware Report created:', data);

        setSubmissionResult({
          success: true,
          caseNumber: data.caseNumber,
          message: 'Report saved successfully! Now opening Google Malware Report Form...'
        });

        // Delay to show success message, then open Google form
        setTimeout(() => {
          window.open(googleFormUrl, '_blank');
        }, 1500);

        // Reset form after 2 seconds
        setTimeout(() => {
          setFormData({
            packageNames: '',
            email: '',
            name: '',
            organization: '',
            isOnPlayStore: 'Yes',
            sha256OrLink: '',
            suspectedCategory: 'Spyware',
            details: '',
            hasPublicationLink: 'No'
          });
          setSubmissionResult(null);
        }, 3000);
      } else {
        setSubmissionResult({
          success: false,
          message: data.error || 'Failed to create report'
        });
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      setSubmissionResult({
        success: false,
        message: 'An error occurred while submitting the report'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openGoogleForm = () => {
    window.open(googleFormUrl, '_blank');
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/tools')} style={styles.backButton}>
        ← Back to Dashboard
      </button>

      <div style={styles.content}>
        <h1 style={styles.title}>Google Malware Reporter</h1>
        <p style={styles.subtitle}>Report potentially harmful applications (PHA) to Google Play Protect</p>

        {showGuide && (
          <div style={styles.guideBox}>
            <div style={styles.guideHeader}>
              <h2>📋 Instructions</h2>
              <button onClick={() => setShowGuide(false)} style={styles.closeButton}>✕</button>
            </div>
            <ul style={styles.guideList}>
              <li>Fill in the form below with all required information about the malicious app</li>
              <li>Your data will be saved locally in our system for tracking</li>
              <li>After clicking submit, Google's official malware report form will open in a new tab</li>
              <li>Complete the Google form with the same information to officially report the app</li>
              <li>The case number will help us track your submission</li>
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Email & Name Row */}
          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
                style={styles.input}
              />
            </div>
          </div>

          {/* Organization */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Your Organization (Optional)</label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
              placeholder="e.g., NGO Name, Company, School, etc."
              style={styles.input}
            />
          </div>

          {/* Package Names */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Package Name(s) *</label>
            <textarea
              name="packageNames"
              value={formData.packageNames}
              onChange={handleInputChange}
              placeholder="e.g., com.foo.app&#10;com.bar.app&#10;com.baz.app&#10;(comma separated or one per line)"
              required
              style={{ ...styles.textarea, minHeight: '80px' }}
            />
          </div>

          {/* Is on Play Store */}
          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Is this app on Google Play Store? *</label>
              <select
                name="isOnPlayStore"
                value={formData.isOnPlayStore}
                onChange={handleInputChange}
                style={styles.select}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>SHA256 or Play Store Link *</label>
              <input
                type="text"
                name="sha256OrLink"
                value={formData.sha256OrLink}
                onChange={handleInputChange}
                placeholder="SHA256 hash or Play Store URL"
                required
                style={styles.input}
              />
            </div>
          </div>

          {/* Suspected Category */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Suspected Category of Malware (PHA) *</label>
            <select
              name="suspectedCategory"
              value={formData.suspectedCategory}
              onChange={handleInputChange}
              style={styles.select}
              required
            >
              {phaCategoryOptions.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <p style={styles.helpText}>
              <a href="https://developers.google.com/android/play-protect/phacategories" target="_blank" rel="noopener noreferrer">
                View full taxonomy →
              </a>
            </p>
          </div>

          {/* Details */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Details of the Issue *</label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleInputChange}
              placeholder="Provide detailed information about why you believe this is malware"
              required
              style={{ ...styles.textarea, minHeight: '120px' }}
            />
          </div>

          {/* Publication Link */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Does this involve a publication? (e.g., blog post, research paper) *</label>
            <select
              name="hasPublicationLink"
              value={formData.hasPublicationLink}
              onChange={handleInputChange}
              style={styles.select}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
            <p style={styles.helpText}>If Yes, provide the link in the Details section above</p>
          </div>

          {/* Submission Result */}
          {submissionResult && (
            <div style={{
              ...styles.resultBox,
              backgroundColor: submissionResult.success ? '#d4edda' : '#f8d7da',
              borderColor: submissionResult.success ? '#28a745' : '#dc3545',
              color: submissionResult.success ? '#155724' : '#721c24'
            }}>
              <strong>{submissionResult.success ? '✓ Success!' : '✗ Error'}</strong>
              <p>{submissionResult.message}</p>
              {submissionResult.caseNumber && (
                <p><strong>Case Number: {submissionResult.caseNumber}</strong></p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div style={styles.buttonGroup}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Submitting...' : '🚀 Submit & Open Google Form'}
            </button>
            <button
              type="button"
              onClick={openGoogleForm}
              style={{ ...styles.button, ...styles.secondaryButton }}
            >
              📋 Open Google Form (Manual)
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '900px',
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif'
  },
  backButton: {
    marginBottom: '20px',
    padding: '10px 15px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  content: {
    backgroundColor: '#f9f9f9',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  title: {
    color: '#333',
    fontSize: '28px',
    marginBottom: '5px',
    margin: '0 0 10px 0'
  },
  subtitle: {
    color: '#666',
    fontSize: '16px',
    marginBottom: '20px'
  },
  guideBox: {
    backgroundColor: '#e7f3ff',
    border: '1px solid #4da6ff',
    borderRadius: '5px',
    padding: '15px',
    marginBottom: '20px'
  },
  guideHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666'
  },
  guideList: {
    margin: '10px 0 0 0',
    paddingLeft: '20px',
    color: '#333'
  },
  form: {
    marginTop: '20px'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '15px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif'
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
    resize: 'vertical'
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'white',
    cursor: 'pointer'
  },
  helpText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '5px'
  },
  resultBox: {
    padding: '15px',
    borderRadius: '4px',
    border: '1px solid',
    marginBottom: '15px'
  },
  buttonGroup: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginTop: '20px'
  },
  button: {
    padding: '12px 20px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  primaryButton: {
    backgroundColor: '#007bff',
    color: 'white'
  },
  secondaryButton: {
    backgroundColor: '#28a745',
    color: 'white'
  }
};

export default GoogleMalwareReporter;
